import SimpleDropdown from '@/components/simple-dropdown';
import { useDialog } from '@/contexts/DialogContext';
import { useSnackbar } from '@/contexts/SnackBarContext';
import { UpdateAmendmentsRecordBody } from '@/models/Amendments.model';
import { AmendmentsService } from '@/services/Amendments.service';
import SponsorService from '@/services/Sponsor.service';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Unstable_Grid2 as Grid,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Typography,
  useTheme
} from '@mui/material';
import { blue } from '@mui/material/colors';
import Divider from '@mui/material/Divider';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import React, { FC, useCallback, useState } from 'react';

import { defaultFilters } from '../AmendmentsPage.route';
import { AmendmentStatus } from './CreateNewAmendmentDialog.component';
import { PlanTier } from './SelectedPlanCard.component';

type PlanAmendmentDetailProps = {
  amendmentId?: number;
  planName: string;
  size?: 'small' | 'large';
  isJPM?: boolean;
  isOnboarding?: boolean;
  isInteractive?: boolean;
  sponsorPlanId: number;
  status?: string;
  updatedFilters: Record<string, any>;
};

export const PlanAmendmentDetail: FC<PlanAmendmentDetailProps> = (
  props: PlanAmendmentDetailProps
) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { openDialog } = useDialog();
  const theme = useTheme();
  const snackbar = useSnackbar();
  const queryClient = useQueryClient();

  const filterQueryKey = props.updatedFilters?.length
    ? props.updatedFilters
    : defaultFilters;

  const sponsorOnboarding = useQuery(
    ['SponsorService.getOnboardingSponsorById', props.sponsorPlanId],
    () => {
      return SponsorService.getOnboardingSponsorById(props.sponsorPlanId);
    },
    {
      enabled: Boolean(props.sponsorPlanId),
      staleTime: Infinity
    }
  );

  const updateAmendment = useMutation(
    ['AmendmentService.updateAmendmentById', props.amendmentId],
    (data: UpdateAmendmentsRecordBody) =>
      AmendmentsService.updateAmendmentById(props.amendmentId, data),
    {
      onError: () => {
        snackbar.showSnackbar({
          message: `Unable to update amendment ${props.amendmentId}`,
          severity: 'error'
        });
      },
      onSuccess: () => {
        snackbar.showSnackbar({
          message: `Successfully updated amendment ${props.amendmentId}`,
          severity: 'success'
        });
        setAnchorEl(null);
      }
    }
  );

  const handleSubmit = useCallback(
    async data => {
      const payload: UpdateAmendmentsRecordBody = {
        id: props.amendmentId,
        isSubmitted: data.isSubmitted,
        status: data.status
      };

      await updateAmendment.mutateAsync(payload);

      await queryClient.invalidateQueries([
        'AmendmentService.getAmendments',
        props.status,
        filterQueryKey
      ]);
      await queryClient.invalidateQueries([
        'AmendmentService.getAmendments',
        AmendmentStatus.COMPLETED,
        filterQueryKey
      ]);
    },
    [props]
  );

  const handleChange = useCallback(() => {
    setAnchorEl(null);
    openDialog({
      actionButtons: {
        cancelButton: {
          children: 'CANCEL'
        },
        submitButton: {
          children: 'SUBMIT'
        }
      },
      onSubmit: async e => {
        handleSubmit({
          isSubmitted: e.changeStatus === AmendmentStatus.COMPLETED,
          status: e.changeStatus
        });
        await queryClient.invalidateQueries([
          'AmendmentService.getAmendments',
          props.status,
          filterQueryKey
        ]);
        await queryClient.invalidateQueries([
          'AmendmentService.getAmendments',
          e.changeStatus,
          filterQueryKey
        ]);
      },
      steps: [
        {
          fields: {
            changeStatus: {
              component: (
                <SimpleDropdown
                  data-testid='change-status-dropdown'
                  fieldId='changeStatus'
                  fieldName='New Status'
                  fieldValues={[
                    AmendmentStatus.NEW_REQUESTS,
                    AmendmentStatus.IN_PROGRESS,
                    AmendmentStatus.PENDING,
                    AmendmentStatus.VESTING,
                    AmendmentStatus.AWAITING_SIGNATURE,
                    AmendmentStatus.FINAL_REVIEW_NEEDED,
                    AmendmentStatus.AWAITING_PLAN_SPEC_UPDATES
                  ]}
                />
              ),
              initialValue: '',
              label: 'New Status'
            }
          },
          headerText: `Current Status: ${props.status}`,
          title: `Change Status for Amendment #${props.amendmentId}`
        }
      ]
    });
  }, [props]);

  const handleCancel = useCallback(async () => {
    const payload: UpdateAmendmentsRecordBody = {
      id: props.amendmentId,
      isSubmitted: false,
      status: AmendmentStatus.CANCELLED_OR_REJECTED
    };

    await updateAmendment.mutateAsync(payload);
    await queryClient.invalidateQueries([
      'AmendmentService.getAmendments',
      props.status,
      filterQueryKey
    ]);
    await queryClient.invalidateQueries([
      'AmendmentService.getAmendments',
      AmendmentStatus.CANCELLED_OR_REJECTED,
      filterQueryKey
    ]);
  }, [props]);

  return (
    <Grid
      container
      data-testid='plan-amendment-detail'
      justifyContent='space-between'
      width='100%'>
      <Grid container direction='column' paddingLeft={1} paddingY={0.5}>
        <Grid>
          <Typography fontSize={props.size === 'small' ? 16 : 18} margin={0}>
            {props.planName}
          </Typography>
        </Grid>

        <Grid
          alignItems='center'
          columnGap={props.size === 'small' ? 0.5 : 1}
          container>
          <Grid container>
            <Link
              fontSize={props.size === 'small' ? 12 : 14}
              href={`/plans/${props.sponsorPlanId}`}>
              {props.sponsorPlanId}
            </Link>
          </Grid>

          {sponsorOnboarding.data?.fields?.planTier && (
            <>
              <Divider flexItem orientation='vertical' variant='middle' />
              <Grid
                alignSelf='center'
                columnGap={props.size === 'small' ? 0.5 : 1}
                container>
                <Typography fontSize={props.size === 'small' ? 12 : 14}>
                  {PlanTier[sponsorOnboarding.data?.fields?.planTier]}
                </Typography>
              </Grid>
            </>
          )}

          {props.isOnboarding && (
            <>
              <Divider flexItem orientation='vertical' variant='middle' />
              <Grid columnGap={props.size === 'small' ? 0.5 : 1} container>
                <Grid alignSelf='center' container>
                  <span
                    style={{
                      color: theme.palette.warning.main,
                      fontSize: props.size === 'small' ? '18px' : '25px'
                    }}>
                    &#9679;
                  </span>
                </Grid>
                <Grid alignSelf='center' container>
                  <Typography fontSize={props.size === 'small' ? 12 : 14}>
                    Onboarding
                  </Typography>
                </Grid>
              </Grid>
            </>
          )}

          {props.isJPM && (
            <>
              <Divider flexItem orientation='vertical' variant='middle' />
              <Grid columnGap={props.size === 'small' ? 0.5 : 1} container>
                <span
                  style={{
                    color: blue[600],
                    fontSize: props.size === 'small' ? '18px' : '25px'
                  }}>
                  &#9679;
                </span>
                <Grid alignSelf='center' container>
                  <Typography fontSize={props.size === 'small' ? 12 : 14}>
                    JPM Plan
                  </Typography>
                </Grid>
              </Grid>
            </>
          )}
        </Grid>
      </Grid>

      {props.isInteractive && (
        <Grid paddingTop={2}>
          <IconButton
            data-testid='expand-amendment-details-btn'
            onClick={() => {}}
            size='small'>
            <ArrowForwardIosIcon
              fontSize='small'
              sx={{ color: theme.palette.grey[600] }}
            />
          </IconButton>

          <IconButton
            data-testid='amendment-details-actions-btn'
            onClick={e => setAnchorEl(e.currentTarget)}
            size='small'>
            <MoreVertIcon
              fontSize='small'
              sx={{ color: theme.palette.grey[600] }}
            />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            data-testid='amendment-details-actions-menu'
            onClose={() => setAnchorEl(null)}
            open={Boolean(anchorEl)}>
            {props.status !== AmendmentStatus.COMPLETED && (
              <MenuItem
                onClick={() =>
                  handleSubmit({
                    isSubmitted: true,
                    status: AmendmentStatus.COMPLETED
                  })
                }>
                Mark as Submitted
              </MenuItem>
            )}
            <MenuItem onClick={handleChange}>Change Status</MenuItem>
            {props.status !== AmendmentStatus.CANCELLED_OR_REJECTED && (
              <MenuItem onClick={handleCancel}>Cancel Request</MenuItem>
            )}
          </Menu>
        </Grid>
      )}
    </Grid>
  );
};
